import React, { useLayoutEffect, useRef, useState } from 'react';
import { FieldError } from '../../core';

import { uiConfig } from '@/config';
import { cn } from '@/libs';

import { ArrayContainer } from '..';
import { GroupRestrictionErrors } from './GroupRestrictionErrors';
import {
  useClearOnNotApplicable,
  useEffectiveAddress,
  useGroupActions,
  useGroupDeleteGuard,
  useMultipleField,
} from '../../hooks';
import type { ArrayFormField } from '../../typings';

import { ArrayAdd } from '../ArrayAdd';
import { Checkbox } from '../inputs';
import { config } from '../../config';
import { getBaseFieldId } from '../../utils';
import './ArrayPresenter.scss';
import { FormFieldAlertMessage } from './FormFieldAlertMessage';

type ArrayPresenterProps = {
  fieldsArray: ArrayFormField;
  disabled?: boolean;
};

/**
 * @internal
 * This component is for internal use only.
 *
 * This component is used to present an array field. It provides functionality to add and remove items from the array.
 */
export const ArrayPresenter: React.FC<ArrayPresenterProps> = ({
  fieldsArray,
  disabled,
}) => {
  const { addGroup, removeGroup, clearGroup, getRemovalImpact } = useGroupActions();

  const [isCollapsed, setIsCollapsed] = useState(false);

  const {
    requiredFieldId,
    isFieldNA,
    isArrayVisible,
    isLastItem,
    setForcedShow,
  } = useMultipleField(fieldsArray);

  useClearOnNotApplicable(fieldsArray, isFieldNA);

  const effectiveAddress = useEffectiveAddress(
    fieldsArray.fieldId,
    fieldsArray.address,
  );

  const containerClasses = cn(
    'array__main-container',
    uiConfig.grid.maxColumnsSpanClass,
  );

  const addItemHandler = (fieldId: string) => () => {
    if (!isArrayVisible) {
      setForcedShow(true);

      // The server turns an optional group's placeholder into its first
      // entry; a question group without a minimum reveals it here only.
      if (fieldsArray.minItems === 0) addGroup(fieldId);
    } else {
      addGroup(fieldId);
    }
  };

  // The trash icon clears a group's only entry instead of removing it.
  const clearsOnDelete = fieldsArray.index === 0 && isLastItem;

  const removeItemHandler = async () => {
    if (clearsOnDelete) {
      clearGroup(fieldsArray.fieldId);
      setForcedShow(false);
    } else {
      await removeGroup(fieldsArray.fieldId);
    }
  };

  // Only a deleted address entry can leave answers pointing at it.
  const loadDeleteImpact = fieldsArray.address
    ? () => getRemovalImpact(fieldsArray.fieldId, clearsOnDelete)
    : undefined;

  const baseFieldId = getBaseFieldId(fieldsArray.fieldId);

  const { allowRemove, minRequiredHint } = useGroupDeleteGuard(fieldsArray);

  const notApplicableField = fieldsArray.notApplicableField;

  // A lone entry offers Not Applicable under its title, as a single group does. The Add bar
  // keeps the box while the group is collapsed, and beside a question.
  const notApplicableOnTop =
    notApplicableField !== undefined &&
    !fieldsArray.questionIfOptional &&
    isArrayVisible &&
    fieldsArray.index === 0 &&
    isLastItem;

  // Ticking swaps the box between the entry and the Add bar, so focus has to follow it.
  const refocusNotApplicable = useRef(false);
  const notApplicableFieldId = notApplicableField?.fieldId;

  const notApplicableChangeHandler = () => {
    refocusNotApplicable.current =
      document.activeElement?.id === notApplicableFieldId;
  };

  // Before paint, so focus never rests on the page body between the swap and the restore.
  useLayoutEffect(() => {
    if (!refocusNotApplicable.current || !notApplicableFieldId) return;

    refocusNotApplicable.current = false;

    // Focus falls to the body when its box unmounts; anywhere else, the provider moved it.
    const focused = document.activeElement;
    if (focused && focused !== document.body) return;

    document.getElementById(notApplicableFieldId)?.focus();
  }, [notApplicableOnTop, notApplicableFieldId]);

  return (
    // data-jump-scroll-only: jumpToField's first focusable descendant here
    // is GroupTitle's delete button, then its collapse toggle — never a
    // field, so a failed submit must not auto-focus either.
    <div
      id={fieldsArray.fieldId}
      className={containerClasses}
      data-jump-scroll-only="true"
    >
      {isArrayVisible && (
        <ArrayContainer
          groupTitle={fieldsArray.entryTitle ?? fieldsArray.title}
          groupSubtitle={isCollapsed ? effectiveAddress : undefined}
          minRequiredHint={minRequiredHint}
          onDelete={removeItemHandler}
          loadDeleteImpact={loadDeleteImpact}
          fieldsArray={fieldsArray}
          disabled={isFieldNA || disabled}
          isCollapsible={fieldsArray.isCollapsible}
          isCollapsed={isCollapsed}
          onCollapse={() => setIsCollapsed(prev => !prev)}
          allowRemove={allowRemove}
          beforeChildren={
            <>
              {fieldsArray.alertMessage && (
                <FormFieldAlertMessage
                  alertMessage={fieldsArray.alertMessage}
                />
              )}
              {notApplicableOnTop && (
                <Checkbox
                  name={notApplicableField.fieldId}
                  label={notApplicableField.label}
                  size="medium"
                  onChange={notApplicableChangeHandler}
                />
              )}
            </>
          }
          afterChildren={
            <GroupRestrictionErrors
              groupFieldId={fieldsArray.fieldId}
              restriction={fieldsArray.restriction}
              fields={fieldsArray.fields}
            />
          }
        />
      )}

      {!isArrayVisible && fieldsArray.alertMessage && (
        <FormFieldAlertMessage alertMessage={fieldsArray.alertMessage} />
      )}

      <ArrayAdd
        fieldsArray={fieldsArray}
        disabled={isFieldNA || disabled}
        visible={isLastItem}
        collapsed={!isArrayVisible}
        isFieldNA={isFieldNA}
        notApplicableOnTop={notApplicableOnTop}
        onAddItem={addItemHandler}
        onNotApplicableChange={notApplicableChangeHandler}
      />

      <FieldError name={requiredFieldId} />

      {fieldsArray.index === 0 && fieldsArray.isRequired && (
        <FieldError
          name={`${baseFieldId}${config.atLeastOneCompleteFieldSuffix}`}
        />
      )}
    </div>
  );
};
